import { useCallback, useEffect, useState } from "react";

const MAP_FALLBACK_WIDTH_PX = 420;

interface UseCompanionMapSizingParams {
	wrapperRef: React.RefObject<HTMLDivElement | null>;
	contentElement: HTMLElement | null;
	showMap: boolean;
	fallbackWidth?: number;
}

/**
 * Resolves the element whose right edge should bound companion map width.
 */
export function getBoundaryElement(
	wrapperElement: HTMLDivElement | null,
): HTMLElement | null {
	if (!wrapperElement) {
		return null;
	}

	const sidebarColumn = wrapperElement.closest(".sticky");
	const productsColumn = sidebarColumn?.parentElement?.lastElementChild;

	if (productsColumn instanceof HTMLElement) {
		return productsColumn;
	}

	const containerElement = wrapperElement.closest(".container");
	return containerElement instanceof HTMLElement ? containerElement : null;
}

/**
 * Tracks companion map width based on the dropdown position and layout boundary.
 */
export function useCompanionMapSizing({
	wrapperRef,
	contentElement,
	showMap,
	fallbackWidth = MAP_FALLBACK_WIDTH_PX,
}: UseCompanionMapSizingParams) {
	const [mapWidth, setMapWidth] = useState(fallbackWidth);

	const updateMapWidth = useCallback(() => {
		const wrapperElement = wrapperRef.current;
		if (!wrapperElement || !contentElement) {
			setMapWidth(fallbackWidth);
			return;
		}

		const boundaryElement = getBoundaryElement(wrapperElement);
		if (!boundaryElement) {
			setMapWidth(fallbackWidth);
			return;
		}

		const contentRect = contentElement.getBoundingClientRect();
		const boundaryRect = boundaryElement.getBoundingClientRect();
		const availableWidth = boundaryRect.right - contentRect.right;
		const nextWidth = Math.max(0, Math.floor(availableWidth));

		setMapWidth(nextWidth);
	}, [contentElement, fallbackWidth, wrapperRef]);

	useEffect(() => {
		if (!showMap || !contentElement) {
			setMapWidth(fallbackWidth);
			return;
		}

		const frame = requestAnimationFrame(updateMapWidth);
		window.addEventListener("resize", updateMapWidth);

		let resizeObserver: ResizeObserver | undefined;
		let currentBoundaryElement: HTMLElement | null = null;

		if (typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => {
				updateMapWidth();
			});
			resizeObserver.observe(contentElement);

			// Function to update boundary element observation
			const updateBoundaryObservation = () => {
				if (currentBoundaryElement) {
					resizeObserver?.unobserve(currentBoundaryElement);
				}

				currentBoundaryElement = getBoundaryElement(wrapperRef.current);
				if (currentBoundaryElement) {
					resizeObserver?.observe(currentBoundaryElement);
				}
			};

			updateBoundaryObservation();

			// Also update on window resize as DOM structure might change
			const handleResizeWithBoundaryUpdate = () => {
				updateBoundaryObservation();
				updateMapWidth();
			};

			window.removeEventListener("resize", updateMapWidth);
			window.addEventListener("resize", handleResizeWithBoundaryUpdate);
		}

		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener("resize", updateMapWidth);
			resizeObserver?.disconnect();
		};
	}, [contentElement, fallbackWidth, showMap, updateMapWidth, wrapperRef]);

	return { mapWidth };
}
